/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import React, { useState } from "react";
import MicIcon from "@mui/icons-material/Mic";
import { IconButton, Stack, Tooltip } from "@mui/material";
import Softphone from "./Softphone";
import { CalendlyEvent } from "../../types/Calendly/CalendlyEvent";
import MicLevel from "./MicLevel";
import { VolumeUp } from "@mui/icons-material";
import { ProductUnion } from "../../types/ProductUnion";

type LegalbirdCallProps = {
  product: ProductUnion;
  calendlyEvent: CalendlyEvent | null;
};

const LegalbirdCall = ({ product, calendlyEvent }: LegalbirdCallProps) => {
  const [audioContext, setAudioContext] = useState<AudioContext | null>(null);
  const [isActive, setIsActive] = useState(false);

  const handleAudioContextStart = async () => {
    if (!audioContext) {
      const newAudioContext = new (window.AudioContext || window.webkitAudioContext)();

      setAudioContext(newAudioContext);

      newAudioContext.resume().then(() => {
        setIsActive(true);
      });
    } else {
      if (audioContext.state === "suspended") {
        audioContext.resume().then(() => {
          setIsActive(true);
        });
      }
    }
  };

  const playSoundTest = () => {
    const ctx = new AudioContext();
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    // Use a sine wave for a smooth sound
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(523.25, ctx.currentTime); // C5 note

    // Fade out the sound gently
    gainNode.gain.setValueAtTime(0.2, ctx.currentTime); // volume
    gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1);

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.start();
    oscillator.stop(ctx.currentTime + 1);
  };

  return (
    <Stack sx={{
      alignItems: "center"
    }}>
      <Stack
        direction={"row"}
        spacing={2}
        sx={{
          justifyContent: "center",
          alignItems: "center"
        }}>
        {!isActive && (
          <IconButton color={"default"} onClick={handleAudioContextStart}>
            <MicIcon fontSize={"large"} />
          </IconButton>
        )}
        {isActive && <MicLevel />}
        <Tooltip title={"Lautsprecher testen"}>
          <IconButton color={"default"} onClick={playSoundTest} sx={{ width: "51px", height: "51px" }}>
            <VolumeUp />
          </IconButton>
        </Tooltip>
      </Stack>
      {isActive && <Softphone product={product} calendlyEvent={calendlyEvent} />}
      {!isActive && (
        <p className={"center"}>Bitte Mikrofon-Button drücken, um das Mikrofon für den Anruf zu aktivieren</p>
      )}
    </Stack>
  );
};

export default LegalbirdCall;
